import React, { useState, useEffect } from "react";
import {
  Button,
  Grid,
  Typography,
  Box,
  Card,
  CardActionArea,
  CardMedia,
  CardContent,
  Stack,
  Skeleton,
  Collapse,
} from "@mui/material";
import Layout from "../../components/LayoutWithHeader.js";
import { useAuth } from "../../context/authContext.js";
import useApi from "../../hooks/useApi.js";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getBrowserRegion } from "../../utils/utils.js";
import { toast } from "react-toastify";
import { updateProfileInCache } from "../../utils/cacheUtils.js";
import RegionSelector from "../../components/RegionSelector.js";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useNavigate, useSearchParams } from "react-router-dom";
import ErrorIcon from "@mui/icons-material/Error";

const ProviderSkeleton = () => (
  <Grid item xs={6} sm={4}>
    <Skeleton variant="rounded" width={"100%"} height={100} />
  </Grid>
);

const SettingsServices = () => {
  // Hooks
  const { user, updateUser } = useAuth();
  const { editProfile, fetchWatchProviders } = useApi();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // New user
  const [searchParams] = useSearchParams();
  const newUser = searchParams.get("newUser") === "true";

  // Preferences
  const [regionPref, setRegionPref] = useState(() =>
    newUser ? getBrowserRegion() : user.preferences.region,
  );

  const [selectedProviderIds, setSelectedProviderIds] = useState(
    user.preferences.watchProviders ?? [],
  );

  // Queries
  const watchProvidersQuery = useQuery({
    queryKey: ["watchProviders", regionPref],
    queryFn: () => fetchWatchProviders(regionPref),
    refetchOnMount: true,
    enabled: !newUser,
  });

  //
  useEffect(() => {
    if (watchProvidersQuery.data) {
      const availableIds = watchProvidersQuery.data.map((p) => p.id);
      setSelectedProviderIds((prev) =>
        prev.filter((id) => availableIds.includes(id)),
      );
    }
  }, [watchProvidersQuery.data]);

  const toggleProvider = (id) => {
    setSelectedProviderIds((prev) =>
      prev.includes(id) ? prev.filter((pid) => pid !== id) : [...prev, id],
    );
  };

  const hasUnsavedChanges =
    regionPref !== user.preferences.region ||
    JSON.stringify([...(user.preferences.watchProviders ?? [])].sort()) !==
      JSON.stringify([...selectedProviderIds].sort());

  const editProfileMutation = useMutation({
    mutationFn: editProfile,
    onSuccess: (data) => {
      updateProfileInCache({
        queryClient,
        newUserDetails: data,
        onUserUpdate: updateUser,
      });
      // Track's service filter offers only your chosen services
      queryClient.invalidateQueries({ queryKey: ["filterOptions"] });
      if (newUser) navigate("/onboarding?newUser=true");
    },
  });

  const handleSave = () => {
    editProfileMutation.mutate({
      regionPref,
      watchProvidersPref: selectedProviderIds,
    });
    if (!newUser) toast.success("Your preferences have been saved");
  };

  const renderProviders = () => {
    if (watchProvidersQuery.isLoading) {
      return Array.from({ length: 6 }).map((_, i) => (
        <ProviderSkeleton key={i} />
      ));
    }

    if (watchProvidersQuery.isError) {
      return (
        <Grid item xs={12}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{
              border: (theme) => `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
              p: 2,
            }}
          >
            <ErrorIcon />
            <Typography variant="body2">
              Failed to load streaming services, please try again
            </Typography>
          </Stack>
        </Grid>
      );
    }

    if (watchProvidersQuery.data.length === 0) {
      return (
        <Grid item xs={12}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{
              border: (theme) => `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
              p: 2,
            }}
          >
            <ErrorIcon />
            <Typography variant="body2">
              No streaming services available in this region
            </Typography>
          </Stack>
        </Grid>
      );
    }

    return watchProvidersQuery.data.map((provider) => {
      const isSelected = selectedProviderIds.includes(provider.id);
      return (
        <Grid item xs={6} sm={4} key={provider.id}>
          <Card
            variant="outlined"
            sx={{
              borderColor: isSelected ? "primary.main" : "divider",
              borderWidth: isSelected ? 2 : 1,
              position: "relative",
              transition: "border-color 0.2s",
              height: "100%",
            }}
          >
            <CardActionArea
              onClick={() => toggleProvider(provider.id)}
              sx={{ height: "100%" }}
            >
              {provider.logo_path && (
                <CardMedia
                  component="img"
                  image={`https://image.tmdb.org/t/p/original${provider.logo_path}`}
                  alt={provider.name}
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 1,
                    mx: "auto",
                    mt: 1,
                    display: "block",
                  }}
                />
              )}
              <CardContent
                sx={{
                  p: 1,
                  textAlign: "center",
                  "&:last-child": { pb: 1 },
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    lineHeight: 1.2,
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                  }}
                >
                  {provider.name}
                </Typography>
              </CardContent>
            </CardActionArea>
            {isSelected && (
              <CheckCircleIcon
                color="primary"
                sx={{
                  position: "absolute",
                  top: 4,
                  right: 4,
                  fontSize: 16,
                }}
              />
            )}
          </Card>
        </Grid>
      );
    });
  };

  return (
    <Layout
      title={newUser ? "Set your region" : "Streaming region & services"}
      showBackButton={!newUser}
      showMenu={!newUser}
    >
      <Collapse in={!newUser && hasUnsavedChanges}>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          justifyContent="center"
          sx={{
            mb: 2,
            p: 1,
            borderRadius: 1,
            bgcolor: "tertiary.main",
            color: "tertiary.contrastText",
          }}
        >
          <ErrorIcon fontSize="small" />
          <Typography variant="body2">You have unsaved changes</Typography>
        </Stack>
      </Collapse>
      <Grid container spacing={3}>
        <Grid item xs={3}>
          <Typography variant="body2" sx={{ textAlign: "right" }}>
            Region
          </Typography>
        </Grid>
        <Grid item xs={9}>
          <RegionSelector
            value={regionPref}
            onChange={(newRegion) => setRegionPref(newRegion)}
          />
          <Typography
            variant="caption"
            sx={{ display: "block", lineHeight: 1.3, mt: 1 }}
          >
            Used to determine streaming availability for movies and TV shows
          </Typography>
        </Grid>

        {!newUser && (
          <>
            <Grid item xs={3}>
              <Typography variant="body2" sx={{ textAlign: "right", mt: 1 }}>
                Your streaming services
              </Typography>
            </Grid>
            <Grid item xs={9}>
              <Grid container spacing={1}>
                {renderProviders()}
              </Grid>
              <Typography
                variant="caption"
                sx={{ display: "block", lineHeight: 1.2, mt: 1 }}
              >
                Used to provide filter options on the track page
              </Typography>
            </Grid>
          </>
        )}
      </Grid>

      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={
            (!newUser && !hasUnsavedChanges) || editProfileMutation.isPending
          }
        >
          {newUser ? "Continue" : hasUnsavedChanges ? "Save" : "All saved"}
        </Button>
      </Box>
    </Layout>
  );
};

export default SettingsServices;
